export default {
  evaluation: {
    common: {
      save: 'Save',
      cancel: 'Cancel',
    },
    enums: {
      evalSetStatus: {
        draft: 'Draft',
        active: 'Active',
      },
      evalSetMode: {
        'single-turn': 'Single-turn',
        'multi-turn': 'Multi-turn',
      },
      evalCaseType: {
        normal: 'Normal',
        boundary: 'Boundary',
        exception: 'Exception',
      },
      experimentType: {
        regression: 'Standard',
        replay: 'Traffic Replay',
      },
      experimentStatus: {
        idle: 'Not Started',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
        gated: 'Gated',
      },
      caseRunStatus: {
        pass: 'Pass',
        fail: 'Fail',
        unknown: 'Unknown',
      },
      failType: {
        natural: 'System Failure',
        business: 'Business Rule Failure',
      },
      scoreSource: {
        rule: 'Rule-based',
        code: 'Code-based',
        llm: 'LLM (Reserved)',
        none: 'None',
      },
      successPreset: {
        'default': 'Standard',
        'strict': 'Strict',
        'format-only': 'Format Only',
        'custom': 'Custom',
      },
      evalNodeType: {
        none: 'Rule Scoring (No Eval Node)',
        code: 'JS Code Scoring',
        llm: 'LLM Scoring (Reserved)',
      },
    },
    hints: {
      lockedDataset: 'This dataset is locked and cannot be edited',
      lockedReason: 'Lock reason: {reason}',
      runningCannotDelete:
        'Running experiment cannot be deleted. Please stop it first.',
      gatedOnlyContinue: 'Only experiments in gated state can be continued',
      llmReserved:
        'LLM scoring is reserved. Currently falling back to rule scoring.',
      aiGenerateReserved:
        'AI generation is a placeholder. Results are not saved.',
      multiTurnReserved:
        'Multi-turn mode is stored only; rounds are not distinguished during execution.',
      replayReserved:
        'Replay execution logic is the same as standard experiment currently.',
      traceEmpty: 'Workflow execution logging is disabled. No trace available.',
    },
    datasets: {
      list: {
        title: 'Dataset Management',
        create: 'New Dataset',
        searchPlaceholder: 'Search dataset name...',
        columns: {
          name: 'Name',
          toolName: 'Related Workflow',
          mode: 'Mode',
          caseCount: 'Cases',
          version: 'Version',
          status: 'Status',
          createdAt: 'Created At',
          createdBy: 'Created By',
          action: 'Action',
        },
        mode: {
          singleTurn: 'Single-turn',
          multiTurn: 'Multi-turn',
        },
        status: {
          active: 'Active',
          draft: 'Draft',
        },
        action: {
          detail: 'Detail',
          delete: 'Delete',
        },
        deleteConfirm:
          'Are you sure to delete this dataset? This cannot be undone.',
        deleteSuccess: 'Dataset deleted',
        deleteFailed: 'Delete failed. Please try again.',
        loadFailed: 'Failed to load dataset list',
      },
      create: {
        name: 'Dataset Name',
        namePlaceholder: 'Enter dataset name',
        toolName: 'Related Workflow',
        toolNamePlaceholder: 'Select workflow (optional)',
        toolVersion: 'Workflow Version',
        toolVersionPlaceholder: 'Select workflow first',
        mode: 'Mode',
        description: 'Description',
        descriptionPlaceholder: 'Enter description (optional)',
        success: 'Dataset created successfully',
        failed: 'Failed to create dataset',
        nameRequired: 'Please enter a dataset name',
        toolNameRequired: 'Please select a workflow',
        toolVersionRequired: 'Please select a workflow version',
      },
      detail: {
        title: 'Dataset Detail',
        editInfo: 'Edit Info',
        editInfoSuccess: 'Info updated successfully',
        editInfoFailed: 'Failed to update info',
        tabs: {
          cases: 'Case List',
          coverage: 'Coverage Analysis',
          history: 'Version History',
        },
        cases: {
          add: 'Add Case',
          import: 'Batch Import',
          aiGenerate: 'AI Generate',
          edit: 'Edit',
          duplicate: 'Duplicate',
          editSuccess: 'Case updated',
          editFailed: 'Failed to update case',
          duplicateSuccess: 'Case duplicated',
          duplicateFailed: 'Failed to duplicate case',
          turns: 'Conversation Turns',
          addTurn: 'Add Turn',
          turnSummary: '{count} turns',
          turnIndex: 'Turn {index}',
          turnUser: 'User',
          turnAssistant: 'Assistant',
          turnUserPlaceholder: 'User message...',
          turnAssistantPlaceholder: 'Assistant reply...',
          turnExpectedScore: 'Expected Score',
          emptyContent: '(empty)',
          columns: {
            input: 'Input',
            referenceOutput: 'Reference Output',
            type: 'Type',
            note: 'Note',
            action: 'Action',
          },
          notePlaceholder: 'Optional supplementary notes for this case',
          deleteConfirm: 'Are you sure to delete this case?',
          deleteSuccess: 'Case deleted',
          deleteFailed: 'Failed to delete case',
          batchDelete: 'Batch Delete',
          batchDeleteConfirm: 'Are you sure to delete {count} selected cases?',
          batchDeleteSuccess: '{count} cases deleted',
          batchDeleteFailed: 'Failed to batch delete cases',
          addSuccess: 'Case added successfully',
          addFailed: 'Failed to add case',
          required: 'Please fill in the input and reference output',
          multiTurnRequired:
            'Please fill in the case label and at least one conversation turn',
          aiGenerateModal: {
            title: 'AI Generate Cases',
            configStep: 'Configure Generation',
            previewStep: 'Preview Results',
            description: 'Scenario Description',
            descriptionPlaceholder:
              'Describe the scenario, e.g. "common customer service questions"',
            referenceDoc: 'Reference Document (Optional)',
            referenceDocPlaceholder:
              'Paste reference content. AI will generate cases based on it.',
            count: 'Count',
            typeDistribution: 'Type Distribution',
            typeDistributionHint: 'Sum of all types must equal the count',
            normal: 'Normal',
            boundary: 'Boundary',
            exception: 'Exception',
            llmConfig: 'LLM Configuration',
            llmConfigPlaceholder: 'Default model',
            llmConfigTip:
              'Select the LLM configuration for generating cases. Defaults to system model if not set.',
            generate: 'Generate',
            generating: 'Generating...',
            previewDesc:
              '{count} case(s) generated (preview). Confirm to save to dataset.',
            previewMeta: 'Model: {model} · Tokens used: {tokens}',
            confirmSave: 'Save Cases',
            regenerate: 'Regenerate',
            saveSuccess: '{count} case(s) saved successfully',
            saveFailed: 'Save failed, please try again',
            generateFailed: 'Generation failed, please try again',
            countRequired: 'Please enter a count',
            distributionMismatch: 'Distribution sum must equal count',
          },
        },
        coverage: {
          title: 'Coverage Analysis',
          normal: 'Normal',
          boundary: 'Boundary',
          exception: 'Exception',
          balanced: 'Balanced',
          warning: 'Needs Improvement',
          insufficient: 'Insufficient Coverage',
          insufficientHint:
            'Boundary + Exception cases are under 10%. Consider adding more edge cases.',
          balancedHint:
            'Case distribution is balanced (Normal 60±10%, Boundary/Exception ≥10% each)',
          empty: 'No cases yet. Add cases to see coverage analysis.',
        },
        import: {
          title: 'Batch Import JSON',
          confirmBtn: 'Import',
          confirmBtnCount: 'Import {count}',
          placeholder:
            'Paste JSON, format:\n{\'{\'}\n  "cases": [\n    {\'{\'} "input": "...", "expected_output": "...", "type": "normal" {\'}\'}\n  ]\n{\'}\'}',
          valid: 'Valid cases',
          invalid: 'Error rows',
          preview: 'Parse Preview',
          success: 'Successfully imported {imported} cases',
          failed: 'Import failed',
          parseError: 'JSON parse error',
          noCases: 'Missing "cases" array',
          rowError: 'Row {line}: {message}',
          inputRequired: '"input" field is required',
          outputRequired: '"expectedOutput" field is required',
          invalidType: '"type" must be normal / boundary / exception',
        },
        history: {
          columns: {
            version: 'Version',
            caseCount: 'Cases',
            createdAt: 'Created At',
            action: 'Action',
          },
        },
        empty: 'No data',
        loadFailed: 'Failed to load dataset detail',
        createVersion: 'Create Snapshot',
        createVersionSuccess: 'Version {version} created',
        createVersionFailed: 'Failed to create snapshot',
        unlock: 'Unlock',
        unlockSuccess: 'Dataset unlocked',
        unlockFailed: 'Failed to unlock dataset',
      },
    },
    experiments: {
      list: {
        title: 'Experiment Management',
        create: 'New Experiment',
        typePlaceholder: 'Experiment Type',
        allTypes: 'All Types',
        searchPlaceholder: 'Search experiment name...',
        columns: {
          name: 'Experiment Name',
          type: 'Type',
          workflow: 'Related Workflow',
          evalSet: 'Dataset',
          status: 'Status',
          successRate: 'Success Rate',
          duration: 'Duration',
          createdBy: 'Created By',
          action: 'Action',
        },
        type: {
          regression: 'Standard',
          replay: 'Traffic Replay',
        },
        status: {
          idle: 'Not Started',
          running: 'Running',
          completed: 'Completed',
          failed: 'Failed',
          gated: 'Gated',
        },
        action: {
          detail: 'Detail',
          delete: 'Delete',
        },
        deleteConfirm:
          'Are you sure to delete this experiment? This cannot be undone.',
        deleteSuccess: 'Experiment deleted',
        deleteFailed: 'Delete failed. Please try again.',
        loadFailed: 'Failed to load experiment list',
      },
      create: {
        name: 'Experiment Name',
        namePlaceholder: 'Enter experiment name',
        type: 'Experiment Type',
        typeRegression: 'Standard (Regression)',
        typeReplay: 'Traffic Replay',
        typeReplayTip:
          'Replays real inputs from historical execution logs — no manual cases needed',
        evalSetId: 'Dataset',
        evalSetIdPlaceholder: 'Select dataset',
        evalSetIdReplayTip:
          'Optional in replay mode — cases are sourced from workflow execution logs',
        toolName: 'Workflow',
        toolNamePlaceholder: 'Select workflow',
        replayConfig: {
          title: 'Replay Configuration',
          sourceWorkflow: 'Source Workflow',
          sourceWorkflowTip:
            'The workflow whose execution logs will be used as replay inputs',
          sourceWorkflowPlaceholder: 'Select source workflow',
          startTime: 'Start Time',
          startTimePlaceholder: 'No limit',
          endTime: 'End Time',
          endTimePlaceholder: 'No limit',
          maxCases: 'Max Cases',
          maxCasesPlaceholder: 'Default 100',
          sourceEnv: 'Source Environment',
          sourceEnvPlaceholder: 'No limit',
          sourceEnvProd: 'Production (prod)',
          sourceEnvStaging: 'Staging',
        },
        llmConfig: 'LLM Configuration',
        llmConfigTip:
          'Select a model configuration for all LLM calls in this experiment (built-in scoring, LLM eval node, etc.). Takes priority over eval node config. Leave empty to use system default.',
        llmConfigPlaceholder: 'Select model config (optional)',
        preset: 'Success Criteria Preset',
        presetTip:
          'default: min_score=0.6; strict: min_score=0.8 + require JSON format; format-only: min_score=0, format check only; custom: user-defined, with eval node support',
        criteria: {
          title: 'Custom Scoring Criteria',
          minScore: 'Min Pass Score',
          minScoreTip: 'A case passes when its score ≥ this value (0~1)',
          minScorePlaceholder: '0~1, e.g. 0.6',
          requireJsonFormat: 'Require JSON output format',
          requireJsonFormatTip:
            'If enabled, a case fails immediately when the workflow output is not valid JSON',
          requiredFields: 'Required fields (comma-separated)',
          requiredFieldsTip:
            'Output JSON must contain all these keys; missing any will reduce the score',
          requiredFieldsPlaceholder: 'e.g. answer,score',
          forbiddenPatterns: 'Forbidden patterns (comma-separated)',
          forbiddenPatternsTip:
            'A case fails if any of these keywords appear in the output',
          forbiddenPatternsPlaceholder: 'e.g. error,sorry',
          maxLatency: 'Max Latency (ms)',
          maxLatencyTip:
            'A case fails if execution time exceeds this value (milliseconds). 0 = no limit.',
          maxLatencyPlaceholder: '0 = no limit, e.g. 5000',
          maxLatencyNoLimit: 'No limit',
          gateThreshold: 'Gate failure threshold',
          gateThresholdTip:
            'Experiment is paused (status→gated) when live failure rate exceeds this value. Set to 0 to disable gating',
          gateThresholdPlaceholder: '0~1, 0 = disabled',
          gateMinCases: 'Gate min cases',
          gateMinCasesTip:
            'Gate check starts only after this many cases, preventing false triggers on small samples',
          gateMinCasesPlaceholder: 'Check gate after N cases',
        },
        evalNode: {
          title: 'Eval Node',
          type: 'Scoring Method',
          typeTip:
            'none: built-in rule scoring; code: custom JS script; llm: LLM scoring (calls a language model)',
          codeScript: 'Scoring Script',
          codeScriptTip:
            'Script has access to: input (case input), expected (reference output), actual (workflow output). Must return { score: 0~1, reason: string }',
          codeLanguage: 'Script Language',
          codeLanguagePlaceholder: 'Select language',
          codeLanguageJavaScript: 'JavaScript',
          codeLanguagePython: 'Python',
          codeScriptPlaceholder:
            '// Receives input / expected / actual\n// Return { score: number, reason: string }\nvar exp = JSON.parse(expected);\nvar act = JSON.parse(actual);\nif (act.answer === exp.answer) return {score:1, reason:"match"};\nreturn {score:0, reason:"mismatch"};',
          codeScriptGenerate: 'AI Generate',
          codeScriptGenerateDesc:
            'Describe the scoring logic, AI will generate the JavaScript script',
          codeScriptGeneratePlaceholder:
            'e.g. Check JSON format, answer field is not empty and contains no sensitive words',
          codeScriptGenerateSuccess: 'Script generated',
          codeScriptGenerateFailed: 'Failed to generate script',
          llmPrompt: 'LLM Eval Prompt',
          llmPromptTip:
            'Describe evaluation criteria. The system will inject input/expected/actual into the prompt and use an LLM to score 0~1',
          llmPromptPlaceholder:
            'e.g. Evaluate accuracy and completeness. Score 0.8+ for correct, 0.4-0.7 for partial...',
          llmPromptGenerate: 'AI Generate',
          llmPromptGenerateDesc:
            'Describe evaluation criteria, AI will generate the LLM eval prompt',
          llmPromptGeneratePlaceholder:
            'e.g. Evaluate the professionalism, accuracy and friendliness of the response',
          llmPromptGenerateSuccess: 'Prompt generated',
          llmPromptGenerateFailed: 'Failed to generate prompt',
          llmConfig: 'LLM Configuration',
          llmConfigTip:
            'Select a LLM config for evaluation. Leave empty to use system default model',
          llmConfigPlaceholder: 'Default model',
        },
        workflowVersion: 'Workflow Version',
        workflowVersionPlaceholder: 'Select version',
        required: 'Please fill in name and select dataset and workflow',
        success: 'Experiment created successfully',
        failed: 'Failed to create experiment',
      },
      detail: {
        title: 'Experiment Detail',
        run: 'Start Batch',
        rerun: 'Re-run',
        stop: 'Stop',
        continue: 'Continue',
        edit: 'Edit Experiment',
        editSuccess: 'Experiment updated',
        editFailed: 'Update failed. Please try again.',
        editDisabledHint: 'Running experiments cannot be edited',
        delete: 'Delete Experiment',
        deleteConfirm:
          'Are you sure to delete this experiment? This cannot be undone.',
        deleteSuccess: 'Experiment deleted',
        deleteFailed: 'Delete failed. Please try again.',
        loadFailed: 'Failed to load experiment detail',
        runSuccess: 'Experiment started',
        runFailed: 'Failed to start experiment',
        stopSuccess: 'Experiment stopped',
        stopFailed: 'Failed to stop experiment',
        continueSuccess: 'Experiment resumed',
        continueFailed: 'Failed to resume experiment',
        tabs: {
          config: 'Configuration',
          results: 'Results',
          analysis: 'Collision Analysis',
        },
        config: {
          title: 'Success Criteria',
          preset: 'Preset Mode',
          evalNode: 'Eval Node',
          workflowVersion: 'Workflow Version',
          mode: {
            default: 'Standard',
            strict: 'Strict',
            formatOnly: 'Format Only',
            custom: 'Custom',
          },
          nodeType: {
            none: 'None (Rule-based)',
            llm: 'LLM Eval',
            code: 'JS Code Scoring',
          },
        },
        results: {
          failureRate: 'Failure Rate',
          naturalFailureCount: 'Natural Failures',
          businessFailureCount: 'Business Failures',
          avgLatency: 'Avg Latency',
          avgToken: 'Avg Tokens',
          errorRate: 'Error Rate',
          loadFailed: 'Failed to load results',
          loadDetailFailed: 'Failed to load result detail',
          runningProgress: 'Running: {done} / {total} cases completed',
          gatedHint:
            'Gate triggered: success rate below threshold. Experiment is paused. You may continue or stop.',
          traceTitle: 'Execution Trace',
          filter: {
            status: 'Result Status',
            failType: 'Fail Type',
            caseType: 'Case Type',
            keyword: 'Search Input',
            all: 'All',
          },
          columns: {
            caseId: 'Case ID',
            input: 'Input',
            expected: 'Expected',
            actual: 'Actual',
            status: 'Status',
            score: 'Score',
            scoreSource: 'Score Source',
            failType: 'Fail Type',
            latency: 'Latency(ms)',
            action: 'Action',
          },
          viewDetail: 'Detail',
        },
        resultDetail: {
          title: 'Result Detail',
          input: 'Input',
          expected: 'Expected',
          actual: 'Actual',
          score: 'Score',
          status: 'Status',
          scoreSource: 'Score Source',
          failType: 'Fail Type',
          failReason: 'Fail Reason',
          llmReason: 'LLM Scoring Reason',
          llmModel: 'Scoring Model',
          evalResult: 'Eval Result',
          trace: 'Trace',
          traceEmpty: 'No trace data (workflow logging is disabled)',
          loadFailed: 'Failed to load result detail',
        },
        analysis: {
          empty: 'No comparison data',
        },
      },
    },
    cost: {
      title: 'Cost Governance',
      selectWorkflow: 'Select Workflow',
      allWorkflows: 'All Workflows',
      stats: {
        totalCost: 'Total Cost (USD)',
        totalTokens: 'Total Tokens',
        avgDailyCost: 'Avg Daily Cost (USD)',
      },
      columns: {
        workflowName: 'Workflow Name',
        calls: 'Calls',
        tokens: 'Total Tokens',
        cost: 'Est. Cost (USD)',
        action: 'Action',
      },
      action: {
        replay: 'Traffic Replay',
      },
    },
  },
};
